import React from "react";
import { Helmet } from "react-helmet";
import { Box, Button, CircularProgress, Stack } from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import styled from "styled-components";
import AnalysisMissionsDrawer from "src/scenes/causal-analysis/analysis-missions-drawer";
import AnalysisTreeProduction from "src/scenes/causal-analysis/analysis-tree-production";
import AnalysisPicker from "src/scenes/causal-analysis/analysis-picker";
import AnalysisNegligibleData from "src/scenes/causal-analysis/analysis-negligible-section";
import { useAuth } from "src/components/providers/auth-context";
import useTeamsApi from "src/scenes/teams/hooks/use-teams-api";
import AnalysisTargetSelect from "src/scenes/causal-analysis/components/analysis-target-select";
import request from "src/utils/request";
import { ToastContext } from "src/components/providers/toast-provider";
import AnalysisGenericModelNotification from "src/scenes/causal-analysis/analysis-generic-model-notification";
import CalculatingCustomModelNotification from "src/scenes/causal-analysis/analysis-calculating-custom-model-notification";
import TourIcon from "@mui/icons-material/Tour";
import { ActiveIntegrationsProvider } from "src/scenes/dashboard/active-integrations-provider";
import Link from "@mui/material/Link";
import Portal from "src/components/portal";
import DemoBarPersonal from "src/components/demo-bar-personal";
import DemoBarDemo from "src/components/demo-bar-demo";
import DemoBarEnterprise from "src/components/demo-bar-enterprise";

const RestartButton = styled(Button)`
    background-color: ${({ theme }) => theme.palette.background.default};
    font-size: 12px;
    z-index: 10;
`;

const CausalBox = styled(Box)`
    background-color: #353535;
    border-radius: 14px;
    padding: 16px;
`;

const CalculateButton = styled(Button)`
    font-size: 12px;
    height: 33px;
`;

const MissionsButton = styled(Button)`
    background-color: ${({ theme }) => theme.palette.background.default};
    font-size: 12px;
    margin-top: 13px;
    z-index: 10;
`;

const CausationAnalysis = () => {
    const [missionsDrawerOpen, setMissionDrawerOpen] = React.useState(false);
    const [interventionData, setInterventionData] = React.useState({});
    const [interventionResponse, setInterventionResponse] = React.useState(undefined);
    const [isDirty, setIsDirty] = React.useState(false);
    const [isCalculated, setIsCalculated] = React.useState(false);
    const [analysisTarget, setAnalysisTarget] = React.useState("yourself");
    const [isTeam, setIsTeam] = React.useState(false);
    const [isOrg, setIsOrg] = React.useState(false);
    const [selectedId, setSelectedId] = React.useState(undefined);
    const [graphData, setGraphData] = React.useState(undefined);

    const { setMessage, setSeverity, setIsToastTriggered } = React.useContext(ToastContext);

    const { user, isDemo } = useAuth();
    const teamsApi = useTeamsApi();
    const isProd = process.env.ENVIRONMENT_NAME === "prod";
    const { isUserPayingCustomer } = useAuth();
    if (!selectedId && user) setSelectedId(user.id);

    const getGraph = async () => {
        let response;
        if (isDemo) {
            response = (await import("./causal-analysis-mock-data")).default;
        } else if (isTeam) {
            response = await request(`/api/data/causal/teams/${selectedId}`, { method: "GET" });
        } else if (isOrg) {
            response = await request(`/api/data/causal/org`, { method: "GET" });
        } else {
            response = await request(`/api/data/causal`, { method: "GET" });
        }
        setGraphData(response);
    };

    const createInterventions = async () => {
        let response;
        if (isDemo) {
            response = (await import("./causal-intervention-mock-data")).default;
        } else if (isTeam && graphData && graphData.isGenericModel) {
            response = await request(`/api/data/causal/interventions`, {
                method: "POST",
                body: JSON.stringify({
                    interventions: interventionData,
                }),
            });
        } else if (isTeam && graphData && !graphData.isGenericModel) {
            response = await request(`/api/data/causal/interventions`, {
                method: "POST",
                body: JSON.stringify({
                    interventions: interventionData,
                    id: `${user.orgId}:${selectedId}`,
                }),
            });
        } else if (isOrg && graphData && graphData.isGenericModel) {
            response = await request(`/api/data/causal/interventions`, {
                method: "POST",
                body: JSON.stringify({
                    interventions: interventionData,
                }),
            });
        } else if (isOrg && graphData && !graphData.isGenericModel) {
            response = await request(`/api/data/causal/interventions`, {
                method: "POST",
                body: JSON.stringify({
                    interventions: interventionData,
                    id: `${user.orgId}:${user.orgId}`,
                }),
            });
        } else if (!isTeam && !isOrg && graphData && graphData.isGenericModel) {
            response = await request(`/api/data/causal/interventions`, {
                method: "POST",
                body: JSON.stringify({
                    interventions: interventionData,
                }),
            });
        } else {
            response = await request(`/api/data/causal/interventions`, {
                method: "POST",
                body: JSON.stringify({
                    interventions: interventionData,
                    id: `${user.orgId}:${user.id}`,
                }),
            });
        }
        setInterventionResponse(response);
        setMessage(`Interventions calculated successfully`);
        setSeverity("success");
        setIsToastTriggered(true);
    };

    React.useEffect(() => {
        if (interventionData && interventionData.length > 0) setIsDirty(true);
        if (interventionData && interventionData.length === 0) setIsDirty(false);
    }, [interventionData]);

    React.useEffect(() => {
        getGraph();
    }, [selectedId]);

    const handleGetGraph = async () => {
        try {
            getGraph();
        } catch (e) {
            setMessage(`Error fetching graph. Please try again.`);
            setSeverity("error");
            setIsToastTriggered(true);
        }
    };

    const notificationHandler = () => {
        if (graphData && !graphData.isGenericModel && graphData && !graphData.modelCalculating)
            return null;
        if (
            graphData &&
            graphData.isGenericModel &&
            graphData &&
            !graphData.modelCalculating &&
            (isUserPayingCustomer || isDemo)
        )
            return (
                <ActiveIntegrationsProvider>
                    <AnalysisGenericModelNotification
                        user={user}
                        isTeam={isTeam}
                        isOrg={isOrg}
                        isDemo={isDemo}
                        selectedId={selectedId}
                        setSelectedId={setSelectedId}
                    />
                </ActiveIntegrationsProvider>
            );
        if (
            graphData &&
            graphData.isGenericModel &&
            graphData &&
            graphData.modelCalculating &&
            isUserPayingCustomer
        )
            return <CalculatingCustomModelNotification />;
        return null;
    };

    const handleCreateInterventions = async () => {
        try {
            createInterventions();
        } catch (e) {
            setMessage(`Error creating intervention. Please try again.`);
            setSeverity("error");
            setIsToastTriggered(true);
        }
    };

    return (
        <>
            <Helmet>
                <title>Adadot - Causal AI</title>
            </Helmet>
            <Portal>
                <ActiveIntegrationsProvider>
                    <DemoBarPersonal />
                    <DemoBarDemo />
                    <DemoBarEnterprise />
                </ActiveIntegrationsProvider>
            </Portal>
            <Stack direction="row" mb={2} alignItems="center" style={{ width: "100%" }}>
                <h1 style={{ width: "360px" }}>Causal AI</h1>
                <CausalBox>
                    <p style={{ margin: 0 }}>
                        You can use{" "}
                        <Link href="https://docs.adadot.com/Data/Causal/" target="_blank">
                            Causal AI
                        </Link>{" "}
                        to see what affects your Lead Time. This covers coding activities but also
                        gluework activities like meetings and things like focus time. The models
                        available are either generic (taking into account our 50k developer
                        datasets) or completely custom to you / your team at the push of a button.
                    </p>
                </CausalBox>
            </Stack>
            {!isProd && <AnalysisPicker handleGetGraph={handleGetGraph} />}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
                mt={2}
                style={{ width: "100%" }}
            >
                <Stack direction="row">
                    <p>The below Causation Analysis is for</p>
                    <AnalysisTargetSelect
                        analysisTarget={analysisTarget}
                        setAnalysisTarget={setAnalysisTarget}
                        setInterventionResponse={setInterventionResponse}
                        user={user}
                        teamsApi={teamsApi}
                        setIsOrg={setIsOrg}
                        setIsTeam={setIsTeam}
                        setSelectedId={setSelectedId}
                        setIsCalculated={setIsCalculated}
                    />
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                    {!isDirty && !isCalculated && (
                        <>
                            <p
                                style={{
                                    margin: 0,
                                    paddingLeft: "8px",
                                    fontSize: "11px",
                                    height: "33px",
                                    paddingTop: "8px",
                                }}
                            >
                                (Drag{" "}
                                <Link
                                    href="https://docs.adadot.com/Data/Causal/interventions/"
                                    target="_blank"
                                >
                                    interventions
                                </Link>{" "}
                                slider bellow and test user scenarios)
                            </p>
                            {!isProd && (
                                <MissionsButton
                                    variant="outlined"
                                    color="secondary"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setMissionDrawerOpen(true);
                                    }}
                                    startIcon={<TourIcon />}
                                    style={{
                                        textDecoration: "auto",
                                        textAlign: "center",
                                        paddingRight: "12px",
                                    }}
                                >
                                    Remediation Actions
                                </MissionsButton>
                            )}
                        </>
                    )}
                    {isCalculated && interventionResponse === undefined && (
                        <Box sx={{ display: "flex" }}>
                            <CircularProgress />
                        </Box>
                    )}
                    {isDirty && (
                        <CalculateButton
                            variant="contained"
                            onClick={() => {
                                setIsCalculated(true);
                                setIsDirty(false);
                                setInterventionResponse(undefined);
                                handleCreateInterventions();
                            }}
                        >
                            Calculate Interventions
                        </CalculateButton>
                    )}
                    {(isDirty || (isCalculated && interventionResponse !== undefined)) && (
                        <RestartButton
                            variant="outlined"
                            startIcon={<RestartAltIcon />}
                            onClick={() => {
                                setInterventionData([]);
                                setInterventionResponse(undefined);
                                setIsCalculated(false);
                            }}
                        >
                            Reset Interventions
                        </RestartButton>
                    )}
                </Stack>
            </Stack>
            {notificationHandler()}
            <AnalysisTreeProduction
                data={graphData && graphData.treeData}
                setIsDirty={setIsDirty}
                isCalculated={isCalculated}
                interventionData={interventionData}
                setInterventionData={setInterventionData}
                interventionResponse={interventionResponse}
            />
            <AnalysisNegligibleData data={graphData && graphData.negligibleData} />
            <AnalysisMissionsDrawer
                open={missionsDrawerOpen}
                setOpen={setMissionDrawerOpen}
                data={graphData && graphData.missions}
            />
        </>
    );
};

export default CausationAnalysis;
